/**
 * MarketIntel.jsx — Cross-Server TTC & ESO-Hub Strategic Market Intelligence Widget.
 *
 * Surfaces four integrated analytical perspectives:
 *   1. Disappearance Lifecycle (SOLD_CONFIRMED, SOLD_IMPLIED, CANCELLED_RELIST, EXPIRED, AT_RISK)
 *   2. ESO-Hub × TTC Velocity (Supply Vacuums, High Gold Turnover, Overpriced Expiry Traps)
 *   3. Arbitrage & Snipe Radar (Underpriced active kiosk listings backed by ESO-Hub liquidity)
 *   4. Guild Kiosk Shelf & Conversion (Side-by-side shelf value, sell-through %, cancel %, expiry %)
 */
import { createSignal, Show, For } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import { gold, qty, relTime, esoIconUrl, ESO_ICON_FALLBACK } from '../lib/format.js';
import { pickItem, pickUser, pickGuild } from '../lib/picks.js';
import './MarketIntel.css';

const TABS = [
  { id: 'lifecycle', label: '⚡ Disappearance Lifecycle' },
  { id: 'macro',     label: '🌊 ESO-Hub × TTC Velocity' },
  { id: 'snipes',    label: '🎯 Snipe & Flip Radar' },
  { id: 'guilds',    label: '🏰 Guild Kiosk Conversion' },
];

const STATUS_OPTIONS = [
  { id: 'ALL_RESOLVED',     label: 'All Resolved' },
  { id: 'SOLD_ALL',         label: '✓ All Sold' },
  { id: 'SOLD_CONFIRMED',   label: '✓ Confirmed' },
  { id: 'SOLD_IMPLIED',     label: '⚡ Implied Sales' },
  { id: 'CANCELLED_RELIST', label: '↺ Cancelled / Undercut' },
  { id: 'EXPIRED',          label: '⌛ Expired (30d)' },
  { id: 'AT_RISK',          label: '⚠ At-Risk Overpriced' },
];

const MACRO_MODES = [
  { id: 'vacuum',   label: '🌪️ Supply Vacuums (High Demand / Low Supply)' },
  { id: 'velocity', label: '🔥 Highest Gold Velocity (PC-NA)' },
  { id: 'spread',   label: '🪤 Overpriced Expiry Traps (List ≫ Sold)' },
];

function statusBadgeLabel(status) {
  switch (status) {
    case 'SOLD_CONFIRMED':   return '✓ Sold';
    case 'SOLD_IMPLIED':     return '⚡ Implied Sale';
    case 'CANCELLED_RELIST': return '↺ Undercut / Relist';
    case 'EXPIRED':          return '⌛ Expired';
    case 'ACTIVE':           return '● Active';
    default:                 return status;
  }
}

export default function MarketIntel() {
  const [activeTab, setActiveTab] = createSignal('lifecycle');
  const [statusFilter, setStatusFilter] = createSignal('ALL_RESOLVED');
  const [macroMode, setMacroMode] = createSignal('vacuum');
  const [guildFilter, setGuildFilter] = createSignal('');
  const [searchInput, setSearchInput] = createSignal('');
  const [committedQuery, setCommittedQuery] = createSignal('');

  let debounceTimer = null;
  function onSearchChange(val) {
    setSearchInput(val);
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      setCommittedQuery(val.trim());
    }, 260);
  }

  const { data, loading, refreshing, error, refetch } = useWidgetData('market-intel', () => ({
    macroMode: macroMode(),
    statusFilter: statusFilter(),
    guildName: guildFilter(),
    query: committedQuery(),
  }));

  const overview = () => data()?.overview || {};
  const lifecycle = () => overview()?.lifecycle || {};
  const guilds = () => data()?.guilds || [];

  return (
    <div class="v2-market-intel">
      {/* ── Top Executive KPI Strip ── */}
      <Show when={overview().totalObservedListings != null}>
        <div class="v2-mi-kpis">
          <div class="v2-mi-kpi v2-mi-kpi--gold">
            <span class="v2-mi-kpi__label">Attributed TTC Sales (Deduplicated)</span>
            <strong class="v2-mi-kpi__val">{gold(overview().attributed?.totalGold || 0)}</strong>
            <span class="v2-mi-kpi__sub">
              {qty(overview().attributed?.netNewSales || 0)} net-new · {qty(overview().attributed?.crossVerifiedSales || 0)} in Guild_Sales
            </span>
          </div>
          <div class="v2-mi-kpi v2-mi-kpi--green">
            <span class="v2-mi-kpi__label">Sell Resolution Rate</span>
            <strong class="v2-mi-kpi__val">{overview().sellResolutionRate || 0}%</strong>
            <span class="v2-mi-kpi__sub">
              {qty(lifecycle().SOLD_CONFIRMED?.count || 0)} confirmed · {qty(lifecycle().SOLD_IMPLIED?.count || 0)} implied
            </span>
          </div>
          <div class="v2-mi-kpi v2-mi-kpi--amber">
            <span class="v2-mi-kpi__label">Cancel &amp; Expire Rate</span>
            <strong class="v2-mi-kpi__val">{overview().cancelRate || 0}% / {overview().expiryRate || 0}%</strong>
            <span class="v2-mi-kpi__sub">
              {qty(lifecycle().CANCELLED_RELIST?.count || 0)} undercut · {qty(lifecycle().EXPIRED?.count || 0)} expired
            </span>
          </div>
          <div class="v2-mi-kpi v2-mi-kpi--cyan">
            <span class="v2-mi-kpi__label">ESO-Hub × TTC Pulse</span>
            <strong class="v2-mi-kpi__val">{qty(overview().esoHub?.trackedItems || 0)} items</strong>
            <span class="v2-mi-kpi__sub">
              {qty(overview().esoHub?.serverTotalSales || 0)} PC-NA sales · TTC v{overview().ttcSnapshot?.currentVersion || '—'}
            </span>
          </div>
        </div>
      </Show>

      {/* ── Navigation & Global Filter Bar ── */}
      <div class="v2-mi-toolbar">
        <div class="v2-mi-tabs" role="tablist" aria-label="Market Intelligence Views">
          <For each={TABS}>{(t) => (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab() === t.id}
              class={`v2-btn-sm v2-mi-tab ${activeTab() === t.id ? 'is-active' : ''}`}
              onClick={() => setActiveTab(t.id)}
            >{t.label}</button>
          )}</For>
        </div>

        <div class="v2-mi-filters">
          <Show when={activeTab() === 'lifecycle' || activeTab() === 'snipes'}>
            <select
              class="v2-mi-select"
              aria-label="Filter by guild"
              value={guildFilter()}
              onChange={(e) => setGuildFilter(e.currentTarget.value)}
            >
              <option value="">All Tracked Guilds</option>
              <For each={guilds()}>{(g) => (
                <option value={g.guildName}>{g.guildName}</option>
              )}</For>
            </select>
          </Show>
          <input
            type="search"
            class="v2-mi-search"
            placeholder="Filter item, @seller, or guild…"
            aria-label="Filter market intelligence items"
            value={searchInput()}
            onInput={(e) => onSearchChange(e.currentTarget.value)}
          />
          <button
            type="button"
            class="v2-btn-sm"
            onClick={() => refetch()}
            disabled={refreshing()}
            title="Refresh Market Intelligence"
          >{refreshing() ? '⟳' : '↻'}</button>
        </div>
      </div>

      <Show when={!loading()} fallback={<div class="v2-widget-skel">Synthesizing TTC &amp; ESO-Hub economy intelligence…</div>}>
        <Show when={!error()} fallback={<div class="v2-error">{error()}</div>}>

          {/* ── TAB 1: Disappearance Lifecycle ── */}
          <Show when={activeTab() === 'lifecycle'}>
            <div class="v2-mi-subbar">
              <For each={STATUS_OPTIONS}>{(opt) => (
                <button
                  type="button"
                  class={`v2-btn-sm ${statusFilter() === opt.id ? 'is-active' : ''}`}
                  onClick={() => setStatusFilter(opt.id)}
                >{opt.label}</button>
              )}</For>
            </div>

            <div class="v2-mi-table-region" tabindex="0" role="region" aria-label="Listing disappearance lifecycle table">
              <table class="v2-mi-table">
                <thead>
                  <tr>
                    <th>Outcome</th>
                    <th>Item</th>
                    <th>Seller</th>
                    <th>Guild / Kiosk</th>
                    <th class="is-num">Listed Price</th>
                    <th class="is-num">vs Market</th>
                    <th class="is-num">Shelf Time</th>
                    <th>Lifecycle Resolution Note</th>
                  </tr>
                </thead>
                <tbody>
                  <Show when={(data()?.disappearances || []).length > 0} fallback={
                    <tr><td colspan="8" class="v2-empty">No listings match this lifecycle filter.</td></tr>
                  }>
                    <For each={data()?.disappearances || []}>{(row) => (
                      <tr>
                        <td>
                          <span class={`v2-ttc-badge v2-ttc-badge--${(row.status || '').toLowerCase()}`}>
                            {statusBadgeLabel(row.status)}
                          </span>
                        </td>
                        <td>
                          <div class="v2-mi-item-cell">
                            <Show when={row.icon}>
                              <img class="v2-item-icon v2-item-icon--xs" src={esoIconUrl(row.icon)} alt=""
                                onError={(e) => { e.currentTarget.src = ESO_ICON_FALLBACK; }} />
                            </Show>
                            <span class="v2-pick v2-pick--item" onClick={() => pickItem(row.itemName, { icon: row.icon })}>
                              {row.itemName}
                            </span>
                            <Show when={row.amount > 1}>
                              <span class="v2-dim">×{row.amount}</span>
                            </Show>
                          </div>
                        </td>
                        <td>
                          <span class="v2-pick v2-pick--user" onClick={() => pickUser(row.sellerName)}>
                            {row.sellerName}
                          </span>
                        </td>
                        <td>
                          <span class="v2-pick v2-pick--guild" onClick={() => pickGuild(row.guildName)}>
                            {row.guildName}
                          </span>
                          <Show when={row.kioskCity}>
                            <span class="v2-mi-subtext"> · {row.kioskCity}</span>
                          </Show>
                        </td>
                        <td class="is-num">
                          <span class="v2-mi-gold">{gold(row.totalPrice)}</span>
                          <Show when={row.amount > 1}>
                            <span class="v2-mi-subtext"> ({gold(row.unitPrice)}/ea)</span>
                          </Show>
                        </td>
                        <td class="is-num">
                          <Show when={row.priceVsMarketRatio} fallback={<span class="v2-dim">—</span>}>
                            <span class={
                              row.priceVsMarketRatio <= 0.85 ? 'v2-mi-ratio--low'
                                : row.priceVsMarketRatio >= 1.35 ? 'v2-mi-ratio--high'
                                : 'v2-mi-ratio--mid'
                            }>
                              {row.priceVsMarketRatio}×
                            </span>
                          </Show>
                        </td>
                        <td class="is-num">
                          <span>{row.hoursOnShelf}h</span>
                          <Show when={row.status === 'ACTIVE'}>
                            <span class="v2-mi-subtext"> ({row.daysRemaining}d left)</span>
                          </Show>
                        </td>
                        <td class="v2-mi-note-cell">
                          {row.resolutionNote || (row.status === 'ACTIVE' ? `Overpriced (${row.priceVsMarketRatio}× ref ${gold(row.marketRefPrice)})` : '—')}
                        </td>
                      </tr>
                    )}</For>
                  </Show>
                </tbody>
              </table>
            </div>
          </Show>

          {/* ── TAB 2: ESO-Hub × TTC Macro Velocity ── */}
          <Show when={activeTab() === 'macro'}>
            <div class="v2-mi-subbar">
              <For each={MACRO_MODES}>{(m) => (
                <button
                  type="button"
                  class={`v2-btn-sm ${macroMode() === m.id ? 'is-active' : ''}`}
                  onClick={() => setMacroMode(m.id)}
                >{m.label}</button>
              )}</For>
            </div>

            <div class="v2-mi-table-region" tabindex="0" role="region" aria-label="ESO-Hub and TTC macro velocity table">
              <table class="v2-mi-table">
                <thead>
                  <tr>
                    <th>Signal</th>
                    <th>Item</th>
                    <th class="is-num">ESO-Hub Sold Avg</th>
                    <th class="is-num">ESO-Hub List Avg</th>
                    <th class="is-num">PC-NA Sold (sltl)</th>
                    <th class="is-num">PC-NA Listed (tl)</th>
                    <th class="is-num">Sell-Through</th>
                    <th class="is-num">TTC 6h Δ Supply</th>
                    <th class="is-num">Est. Gold Flow</th>
                  </tr>
                </thead>
                <tbody>
                  <Show when={(data()?.macroItems || []).length > 0} fallback={
                    <tr><td colspan="9" class="v2-empty">No items match this velocity mode.</td></tr>
                  }>
                    <For each={data()?.macroItems || []}>{(it) => (
                      <tr>
                        <td>
                          <span class={`v2-mi-sig v2-mi-sig--${(it.signal || 'BALANCED').toLowerCase()}`}>
                            {it.signal === 'SUPPLY_VACUUM' ? '🌪️ Vacuum'
                              : it.signal === 'HIGH_VELOCITY' ? '🔥 High Velocity'
                              : it.signal === 'EXPIRY_TRAP' ? '🪤 Expiry Trap'
                              : '● Balanced'}
                          </span>
                        </td>
                        <td>
                          <div class="v2-mi-item-cell">
                            <Show when={it.icon}>
                              <img class="v2-item-icon v2-item-icon--xs" src={esoIconUrl(it.icon)} alt=""
                                onError={(e) => { e.currentTarget.src = ESO_ICON_FALLBACK; }} />
                            </Show>
                            <span class="v2-pick v2-pick--item" onClick={() => pickItem(it.itemName, { icon: it.icon })}>
                              {it.itemName}
                            </span>
                          </div>
                        </td>
                        <td class="is-num v2-mi-gold">{gold(it.esoHubSoldAvg)}</td>
                        <td class="is-num">
                          <span>{gold(it.esoHubListAvg)}</span>
                          <Show when={it.listPremiumPct > 15}>
                            <span class="v2-mi-ratio--high"> (+{it.listPremiumPct}%)</span>
                          </Show>
                        </td>
                        <td class="is-num">{qty(it.esoHubSales)}</td>
                        <td class="is-num">{qty(it.esoHubListings)}</td>
                        <td class="is-num">
                          <span class={it.sellThroughRatio >= 1.5 ? 'v2-mi-ratio--low' : it.sellThroughRatio < 0.4 ? 'v2-mi-ratio--high' : ''}>
                            {it.sellThroughRatio}×
                          </span>
                        </td>
                        <td class="is-num">
                          <Show when={it.deltaListings6h !== 0} fallback={<span class="v2-dim">0</span>}>
                            <span class={it.deltaListings6h < 0 ? 'v2-mi-ratio--low' : 'v2-mi-ratio--mid'}>
                              {it.deltaListings6h > 0 ? `+${qty(it.deltaListings6h)}` : qty(it.deltaListings6h)}
                              {' '}({it.deltaListingsPct > 0 ? `+${it.deltaListingsPct}%` : `${it.deltaListingsPct}%`})
                            </span>
                          </Show>
                        </td>
                        <td class="is-num v2-mi-gold">{gold(it.estGoldTurnover)}</td>
                      </tr>
                    )}</For>
                  </Show>
                </tbody>
              </table>
            </div>
          </Show>

          {/* ── TAB 3: Arbitrage & Snipe Radar ── */}
          <Show when={activeTab() === 'snipes'}>
            <div class="v2-mi-table-region" tabindex="0" role="region" aria-label="Arbitrage and snipe radar table">
              <table class="v2-mi-table">
                <thead>
                  <tr>
                    <th>Discount</th>
                    <th>Item</th>
                    <th>Kiosk / Guild</th>
                    <th>Seller</th>
                    <th class="is-num">List Unit Price</th>
                    <th class="is-num">ESO-Hub Sold Ref</th>
                    <th class="is-num">Total Buy Cost</th>
                    <th class="is-num">Est. Net Flip Profit</th>
                    <th class="is-num">PC-NA Sales</th>
                  </tr>
                </thead>
                <tbody>
                  <Show when={(data()?.snipes || []).length > 0} fallback={
                    <tr><td colspan="9" class="v2-empty">No active snipe opportunities match your filter.</td></tr>
                  }>
                    <For each={data()?.snipes || []}>{(s) => (
                      <tr>
                        <td>
                          <span class="v2-ttc-badge v2-ttc-badge--sold_confirmed">
                            −{s.discountPct}% ({s.priceVsMarketRatio}×)
                          </span>
                        </td>
                        <td>
                          <div class="v2-mi-item-cell">
                            <Show when={s.icon}>
                              <img class="v2-item-icon v2-item-icon--xs" src={esoIconUrl(s.icon)} alt=""
                                onError={(e) => { e.currentTarget.src = ESO_ICON_FALLBACK; }} />
                            </Show>
                            <span class="v2-pick v2-pick--item" onClick={() => pickItem(s.itemName, { icon: s.icon })}>
                              {s.itemName}
                            </span>
                            <Show when={s.amount > 1}>
                              <span class="v2-dim">×{s.amount}</span>
                            </Show>
                          </div>
                        </td>
                        <td>
                          <span class="v2-pick v2-pick--guild" onClick={() => pickGuild(s.guildName)}>
                            {s.guildName}
                          </span>
                          <Show when={s.kioskCity}>
                            <span class="v2-mi-subtext"> · {s.kioskCity}</span>
                          </Show>
                        </td>
                        <td>
                          <span class="v2-pick v2-pick--user" onClick={() => pickUser(s.sellerName)}>
                            {s.sellerName}
                          </span>
                        </td>
                        <td class="is-num">{gold(s.unitPrice)}</td>
                        <td class="is-num v2-mi-gold">{gold(s.marketRefPrice)}</td>
                        <td class="is-num">{gold(s.totalPrice)}</td>
                        <td class="is-num">
                          <strong class="v2-mi-ratio--low">+{gold(s.estNetProfit)}</strong>
                        </td>
                        <td class="is-num">{qty(s.esoHubSales)} ({s.sellThroughRatio}×)</td>
                      </tr>
                    )}</For>
                  </Show>
                </tbody>
              </table>
            </div>
          </Show>

          {/* ── TAB 4: Guild Kiosk Shelf & Conversion ── */}
          <Show when={activeTab() === 'guilds'}>
            <div class="v2-mi-table-region" tabindex="0" role="region" aria-label="Guild kiosk shelf conversion table">
              <table class="v2-mi-table">
                <thead>
                  <tr>
                    <th>Guild</th>
                    <th>Kiosk Location</th>
                    <th class="is-num">Active Shelf</th>
                    <th class="is-num">Shelf Gold</th>
                    <th class="is-num">Sell Conversion</th>
                    <th class="is-num">Cancel / Undercut</th>
                    <th class="is-num">Expired (30d)</th>
                    <th class="is-num">Overpriced Risk</th>
                    <th class="is-num">Attributed TTC Gold</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={guilds()}>{(g) => (
                    <tr>
                      <td>
                        <span class="v2-pick v2-pick--guild" onClick={() => pickGuild(g.guildName)}>
                          {g.guildName}
                        </span>
                      </td>
                      <td>
                        {g.kioskCity ? `${g.kioskCity} (${g.kioskZone})` : '—'}
                      </td>
                      <td class="is-num">
                        {qty(g.activeListings)} <span class="v2-mi-subtext">({qty(g.uniqueSellers)} sellers)</span>
                      </td>
                      <td class="is-num v2-mi-gold">{gold(g.totalListedGold)}</td>
                      <td class="is-num">
                        <span class="v2-mi-ratio--low">{g.sellThroughPct}%</span>
                        <span class="v2-mi-subtext"> ({qty((g.counts?.SOLD_CONFIRMED || 0) + (g.counts?.SOLD_IMPLIED || 0))})</span>
                      </td>
                      <td class="is-num">
                        <span>{g.cancelPct}%</span>
                        <span class="v2-mi-subtext"> ({qty(g.counts?.CANCELLED_RELIST || 0)})</span>
                      </td>
                      <td class="is-num">
                        <span class={g.expiryPct >= 50 ? 'v2-mi-ratio--high' : ''}>{g.expiryPct}%</span>
                        <span class="v2-mi-subtext"> ({qty(g.counts?.EXPIRED || 0)})</span>
                      </td>
                      <td class="is-num">{qty(g.overpricedCount || 0)}</td>
                      <td class="is-num v2-mi-gold">
                        {gold(g.attributedSalesGold)}
                        <span class="v2-mi-subtext"> ({qty(g.attributedSalesCount)})</span>
                      </td>
                    </tr>
                  )}</For>
                </tbody>
              </table>
            </div>
          </Show>

        </Show>
      </Show>
    </div>
  );
}
